/** Shared copy: app chrome, sign-in, launcher, access, errors, statuses. */
export const enCore = {
  // ---- Product & interfaces ----------------------------------------------------------
  'app.name': 'apis',
  'app.tagline': 'Restaurant operations',
  'iface.kasir.name': 'Cashier',
  'iface.kasir.description': 'Tables, orders, bills, cash payments and the cash shift.',
  'iface.pelayan.name': 'Service',
  'iface.pelayan.description': 'Floor, table sessions, order taking and serving.',
  'iface.dapur.name': 'Kitchen display',
  'iface.dapur.description': 'Live tickets per station with timers and one-touch status.',
  'iface.backoffice.name': 'Back office',
  'iface.backoffice.description': 'Sales, menu, floors, staff, approvals and branch settings.',
  'iface.keuangan.name': 'Finance & accounting',
  'iface.keuangan.description': 'Cash shifts, supplier payables, budgets and reconciliation.',
  'iface.stok.name': 'Inventory',
  'iface.stok.description': 'Stock on hand, valuation, stock ledger and opening balances.',
  'iface.gudang.name': 'Warehouse',
  'iface.gudang.description':
    'Receiving and inspection, putaway, picking and issue to the kitchen.',
  'iface.menu.name': 'Guest menu (QR)',
  'iface.menu.description':
    'Print table QR codes. Guests scan them for the menu, promotions and paying in the app.',

  // ---- Common ------------------------------------------------------------------------
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.create': 'Create',
  'common.creating': 'Creating…',
  'common.edit': 'Edit',
  'common.remove': 'Remove',
  'common.removing': 'Removing…',
  'common.confirm': 'Confirm',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.optional': 'optional',
  'common.none': 'None',
  'common.all': 'All',
  'common.table.search': 'Search the table',
  'common.table.reset': 'Reset',
  'common.table.export': 'Export CSV',
  'common.table.exportSelected': 'Export {count} selected',
  'common.table.clearSelection': 'Clear selection',
  'common.table.selectAll': 'Select all rows shown',
  'common.table.selectRow': 'Select {row}',
  'common.table.sortBy': 'Sort by {column}',
  'common.table.count': '{count} rows',
  'common.table.shownOf': '{shown} of {total} rows',
  'common.table.selectedOf': '{selected} of {total} rows selected',
  'common.table.filterCount': '{label} ({count})',
  'common.table.rowActions': 'Actions for {name}',
  'common.table.noMatch': 'No rows match the search or filters.',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.signOut': 'Sign out',
  'common.signingOut': 'Signing out…',
  'common.account': 'Account',
  'common.language': 'Language',
  'common.languageName.en': 'English',
  'common.languageName.id': 'Bahasa Indonesia',
  'common.languageShort.en': 'EN',
  'common.languageShort.id': 'ID',
  'common.requestId': 'Reference: {id}',
  'common.skipToContent': 'Skip to main content',
  'common.mainNavigation': 'Main navigation',
  'common.allInterfaces': 'All interfaces',
  'common.switchInterface': 'Switch interface',
  'common.minutes': '{count} min',
  'common.print': 'Print',

  // ---- States ------------------------------------------------------------------------
  'state.laterPhase.badge': 'PHASE {phase}',
  'state.laterPhase.title': '{feature} arrives in Phase {phase}',
  'state.laterPhase.description':
    'This interface is planned for Phase {phase} of the roadmap. Nothing is shown here until it works with real data.',

  // ---- Errors (ApiError code mapping) -------------------------------------------------
  'error.generic': 'The request could not be completed. Try again.',
  'error.network': 'The server could not be reached. Check the connection and try again.',
  'error.validation': 'Some fields are invalid. Check them and try again.',
  'error.conflict': 'This conflicts with existing data.',
  'error.billActive': 'This order has a bill. Void the bill first.',
  'error.billHasPayments': 'This bill already has payments. Issue a refund instead.',
  'error.itemsServed': 'Some items were already served. Issue a refund instead.',
  'error.versionConflict':
    'Someone else changed this at the same time. The latest data has been loaded — check it and try again.',
  'error.forbidden': 'Your role does not allow this action.',
  'error.notFound': 'Not found, or not available yet on this server.',
  'error.unauthenticated': 'Your session has ended. Sign in again.',
  'error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'error.inProgress': 'The previous attempt is still being processed. Wait a moment.',
  'error.budgetExceeded': 'This exceeds the available budget.',
  'error.approvalRequired': 'This needs an approval first.',
  'error.loadFailed': 'Could not load data.',

  // ---- Realtime ----------------------------------------------------------------------
  'realtime.live': 'Live',
  'realtime.offline': 'Offline',
  'realtime.reconnecting': 'Reconnecting…',
  'realtime.connecting': 'Connecting…',
  'realtime.status': 'Live updates: {status}',
  'realtime.hint': 'Screens also refresh on a timer; live updates only make changes appear sooner.',

  // ---- Sign in -----------------------------------------------------------------------
  'auth.hero.title': 'Every table, ticket and bill in one place.',
  'auth.hero.subtitle': 'Cashier, kitchen, service and back office for full-service restaurants.',
  'auth.signIn.title': 'Sign in',
  'auth.signIn.subtitle': 'Use your staff account.',
  'auth.signIn.email': 'Email',
  'auth.signIn.password': 'Password',
  'auth.signIn.submit': 'Sign in',
  'auth.signIn.submitting': 'Signing in…',
  'auth.signIn.error.emailRequired': 'Enter your email address.',
  'auth.signIn.error.emailInvalid': 'Enter a valid email address.',
  'auth.signIn.error.passwordRequired': 'Enter your password.',
  'auth.signIn.error.invalidCredentials': 'The email or password is incorrect.',
  'auth.signIn.error.rateLimited': 'Too many sign-in attempts. Wait a minute and try again.',
  'auth.signIn.error.generic': 'Sign-in failed. Try again.',

  // ---- Access, tenant & branch -------------------------------------------------------
  'access.denied.title': 'No access to {app}',
  'access.denied.description':
    'Your role does not include this interface in any branch you can access. Ask a manager to assign a suitable role.',
  'access.noTenant.title': 'No active organization',
  'access.noTenant.description':
    'Your account is not an active member of any organization. Ask an owner to invite you.',
  'tenant.label': 'Organization',
  'tenant.switch': 'Switch organization',
  'tenant.picker.title': 'Choose an organization',
  'tenant.picker.description':
    'Your account belongs to several organizations. Choose the one you want to work in.',
  'tenant.switching': 'Switching…',
  'tenant.switched': 'Switched to {name}.',
  'branch.label': 'Branch',
  'branch.switch': 'Switch branch',
  'branch.picker.title': 'Choose a branch',
  'branch.picker.description': 'Only branches where you can use {app} are listed.',
  'branch.option': '{name} ({code})',

  // ---- Launcher ----------------------------------------------------------------------
  'launcher.title': 'Where are you working today?',
  'launcher.subtitle': 'Interfaces your role allows in {branch}.',
  'launcher.subtitleNoBranch': 'Interfaces your role allows.',
  'launcher.open': 'Open {name}',
  'launcher.later': 'Later phases',
  'launcher.none':
    'Your role does not include any interface in this branch. Ask a manager to assign a role.',
  'launcher.signedInAs': 'Signed in as {name}',

  // ---- Statuses (rendered in badges as enum caps) ------------------------------------
  'status.table.available': 'Available',
  'status.table.reserved': 'Reserved',
  'status.table.seated': 'Seated',
  'status.table.ordering': 'Ordering',
  'status.table.in_service': 'In service',
  'status.table.payment_pending': 'Awaiting payment',
  'status.table.cleaning': 'Cleaning',
  'status.table.out_of_service': 'Out of service',
  'status.item.draft': 'Draft',
  'status.item.sent': 'Sent',
  'status.item.preparing': 'Preparing',
  'status.item.ready': 'Ready',
  'status.item.served': 'Served',
  'status.item.cancelled': 'Cancelled',
  'status.ticket.new': 'New',
  'status.ticket.acknowledged': 'Acknowledged',
  'status.ticket.preparing': 'Preparing',
  'status.ticket.ready': 'Ready',
  'status.ticket.served': 'Served',
  'status.ticket.cancelled': 'Cancelled',
  'status.bill.open': 'Open',
  'status.bill.settled': 'Settled',
  'status.bill.void': 'Void',
  'status.shift.open': 'Open',
  'status.shift.closed': 'Closed',
  'status.command.pending': 'Sending…',
  'status.command.confirmed': 'Saved',
  'status.command.failed': 'Failed',
} as const;
